import { useReducer } from 'react';
import { Button } from '@/components/ui/Button';
import { useComposerDraftContext, isCurrentDraftContext } from '@/features/coach-review/composerDrafts';
import { listFeedbackContinuations } from '@/lib/feedbackAuthoringContinuation';
import type { FeedbackAuthoringDraftItem, FeedbackDraftScope } from '@/lib/types';
import { useFeedbackAuthoringScope } from '../hooks/useFeedbackAuthoringScope';

export type FeedbackContinuationTarget = { entryId: string; workspaceId?: string; draftId?: string };
export function FeedbackContinuationResume({ scope, active = true, onResume, recoveryOnly = false, discovered }: {
  scope: FeedbackDraftScope; active?: boolean; recoveryOnly?: boolean; discovered?: FeedbackAuthoringDraftItem[]; onResume: (target: FeedbackContinuationTarget) => void;
}) {
  const context = useComposerDraftContext(), authorized = useFeedbackAuthoringScope(scope);
  const [, refresh] = useReducer(value => value + 1, 0);
  if (!active || !authorized || !context || context.actorId !== scope.actorId || context.clubId !== scope.clubId
    || context.teamId !== scope.teamId || context.seasonId !== scope.seasonId) return null;
  let rows: ReturnType<typeof listFeedbackContinuations>;
  try { rows = listFeedbackContinuations(context); }
  catch { return <div role="alert" className="space-y-2 text-sm">Kept work could not be read. Nothing has been sent.
    <Button variant="outline" onClick={refresh}>Retry kept work</Button></div>; }
  if (recoveryOnly) rows = rows.filter(row => row.pending || discovered && !discovered.some(saved =>
    row.workspace_id ? saved.workspace_id === row.workspace_id : saved.feedback_id === row.feedback_id));
  if (!rows.length) return null;
  return <section aria-label="Kept work" className="space-y-2">
    <h3 className="text-sm font-semibold">Drafts in this tab</h3>
    <p className="text-xs text-ink-muted">Resume kept changes or check an unconfirmed save. Opening a draft sends nothing.</p>
    <ul className="divide-y divide-rule">{rows.map((row, index) => <li key={row.entry_id} className="flex items-center gap-3 py-3">
      <div className="min-w-0 flex-1"><p className="text-sm font-semibold">{row.pending ? 'Original operation to check' : 'Kept draft'}</p>
        <p className="text-xs text-ink-muted">{row.pending ? 'Result not confirmed' : 'May include unsaved changes · access checked when opened'}</p></div>
      <Button variant="outline" aria-label={`Resume kept work ${index + 1}`} onClick={() => { if (isCurrentDraftContext(context)) onResume({ entryId: row.entry_id,
        workspaceId: row.workspace_id ?? undefined, draftId: row.feedback_id ?? undefined }); }}>Resume</Button>
    </li>)}</ul>
  </section>;
}
